import capitalize from 'lodash/capitalize';
import { toUnicode } from 'punycode.js';
import { useTranslator } from '@gandi/translate';
import { useAppSelector } from '../../../../../hooks/useAppSelector';
import { selectCartTotal } from '../../../../../modules/selectors';
import { TextPlaceholder } from '../../../../helpers';
import { getPhaseNameTranslation, shouldShowPhase } from './domain';
import { getMemoryTranslation, getStorageTranslation } from './webhosting';
const EMPTY_ARRAY = [];
export function useProductDetails({
  product: {
    productType,
    payload = {},
    identifier,
    prices,
    process,
    isPremium = false
  }
}) {
  const translator = useTranslator();
  const {
    __
  } = translator;
  if (process === 'change_owner' && payload.owner) {
    return [{
      label: __('New owner: %(firstname)s %(lastname)s', {
        firstname: payload.owner.given,
        lastname: payload.owner.family
      })
    }];
  }
  const {
    duration,
    tld_phase
  } = payload;
  if (productType === 'domain') {
    const domainDetails = [];
    if (isPremium) domainDetails.push({
      label: __('Premium')
    });
    if (tld_phase && shouldShowPhase(payload)) domainDetails.push({
      label: getPhaseNameTranslation({
        translator,
        tld_phase
      })
    });
    if (duration != null && duration > 1) {
      const durationLabel = __('%(duration)s years', {
        duration
      });
      const changeLabel = __(', you will be able to modify this duration before payment');
      domainDetails.push({
        label: ['renew', 'restore'].includes(process) ? `+ ${durationLabel}${changeLabel}` : `${durationLabel}${changeLabel}`
      });
    }
    return domainDetails;
  }
  if (payload.fqdn && (productType === 'marketplace' || productType === 'paas' && payload.configuration === 'wordpress')) {
    return [{
      label: __('for %(fqdn)s', {
        fqdn: toUnicode(payload.fqdn)
      }),
      isFqdn: true
    }];
  }
  if (productType === 'cloud' && payload.isFirstServer) {
    const label = [payload.extra?.system, payload.server && payload.server.cores ? __('%(count)s CPUs', {
      count: payload.server.cores
    }) : null, payload.server && payload.server.memory ? getMemoryTranslation({
      translator,
      memory: payload.server.memory
    }) : null, payload.server && payload.server.storage ? getStorageTranslation({
      translator,
      storage: payload.server.storage
    }) : null].filter(Boolean).join(', ');
    return [{
      label
    }];
  }
  if (productType === 'paas') {
    if (process === 'create' && payload.product_name === 'webhosting' && typeof payload.size === 'string') {
      const sizeLabel = capitalize(payload.size);
      if (!payload.domain?.fqdn) return [{
        label: sizeLabel
      }];
      const fqdnLabel = __('With %(fqdn)s', {
        fqdn: toUnicode(payload.domain.fqdn || '')
      });
      return [{
        label: sizeLabel
      }, {
        label: fqdnLabel,
        isFqdn: true
      }];
    }
    if (payload.extra && payload.extra.application && payload.extra.fqdn) {
      const urlPath = payload.extra.vhost ? [payload.extra.vhost, payload.extra.fqdn].join('.') : payload.extra.fqdn;
      return [{
        label: __('On %(fqdn)s', {
          fqdn: urlPath
        }),
        isFqdn: true
      }];
    }
    return [{
      label: payload.shopping?.label || payload.label || ''
    }];
  }
  if (productType === 'certificate' && payload.cn) {
    return [{
      label: __('On %(domain)s', {
        domain: payload.cn
      }),
      isFqdn: true
    }];
  }
  if (productType === 'mailboxv2') {
    const mailbox_details = [];
    if (process === 'renew') mailbox_details.push({
      label: __('%(name)s %(capacity)s', {
        name: payload.name || TextPlaceholder,
        capacity: payload.capacity || TextPlaceholder
      })
    });else mailbox_details.push({
      label: __('%(capacity)s - for %(fqdn)s', {
        capacity: payload.capacity || (payload.mailbox_type ? payload.mailbox_type === 'standard' ? __('3GB') : __('50GB') : TextPlaceholder),
        fqdn: payload.fqdn || TextPlaceholder
      }),
      isFqdn: !!payload.fqdn
    });
    if (duration == null) return mailbox_details;
    if (duration === 1) mailbox_details.push({
      label: __('monthly subscription')
    });else mailbox_details.push({
      label: __('yearly subscription')
    });
    return mailbox_details;
  }
  if (productType === 'mailbox-standalone') {
    const mailbox_details = [];
    if ('duration' in payload && duration === 12 || 'duration_month' in payload && payload.duration_month === 12) mailbox_details.push({
      label: __('Billed yearly')
    });else mailbox_details.push({
      label: __('Billed monthly')
    });
    if (process !== 'create') {
      const description = prices?.[0]?.description;
      if (description) mailbox_details.push({
        label: capitalize(description)
      });
    }
    return mailbox_details;
  }
  if (productType === 'domain_option' && payload.option === 'premium_dns' && identifier) {
    return [{
      label: __('for %(fqdn)s', {
        fqdn: identifier
      }),
      isFqdn: true
    }, {
      label: prices[0].duration === 12 ? __('Billed yearly') : __('Billed monthly')
    }];
  }
  return EMPTY_ARRAY;
}
export function useDiscountDetails({
  product,
  isWidget
}) {
  const {
    __
  } = useTranslator();
  const cartTotal = useAppSelector(selectCartTotal);
  const cartDiscounts = cartTotal.discounts || [];
  if (!product?.prices) {
    return EMPTY_ARRAY;
  }
  const discountedPrice = product.prices.filter(p => p.item_type === 'discount');
  if (discountedPrice.length) {
    const discountDetails = discountedPrice.reduce((acc, productDiscount) => {
      const cartDiscount = cartDiscounts.find(c => c.code === productDiscount.discount_code);
      const displayedCode = productDiscount.discount_code || cartDiscount?.code || TextPlaceholder;
      acc.push({
        label: cartDiscount?.label || (isWidget ? displayedCode : __('Discount code: %(code)s', {
          code: displayedCode
        })),
        price: productDiscount.base_price,
        isPromo: true
      });
      return acc;
    }, []);
    return discountDetails;
  }
  return EMPTY_ARRAY;
}
export const useDetails = ({
  product,
  isWidget = false
}) => {
  const productDetails = useProductDetails({
    product
  });
  const discountDetails = useDiscountDetails({
    product,
    isWidget
  });
  return productDetails.concat(discountDetails);
};